import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Search, ChevronRight, ChevronLeft, CheckCircle, AlertTriangle,
  Loader2, Globe, Users, Database, ClipboardList, Ban
} from 'lucide-react'
import { assessmentsApi, appsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/store/auth'
import { useEntitlements } from '@/hooks/use-entitlements'
import { QuotaBanner } from '@/components/QuotaBanner'
import { cn } from '@/lib/utils'
import type { AssessmentFormData, AppCategory, AssessmentContext, UserType } from '@/types'

// ── Validation Schema ──────────────────────────────────────────────────────────

const schema = z.object({
  prohibited_practices: z.object({
    manipolazione_subliminale: z.boolean().default(false),
    sfruttamento_vulnerabilita: z.boolean().default(false),
    social_scoring: z.boolean().default(false),
    scraping_facciale: z.boolean().default(false),
    riconoscimento_emozioni: z.boolean().default(false),
    categorizzazione_biometrica: z.boolean().default(false),
  }),
  app_name: z.string().min(2, 'Nome richiesto'),
  vendor: z.string().optional(),
  website_url: z.string().url('URL non valido').optional().or(z.literal('')),
  category: z.enum(['TUTORING_IA', 'GENERAZIONE_CONTENUTI', 'VALUTAZIONE_AUTOMATICA', 'ROBOTICA_LAB', 'AMMINISTRAZIONE', 'ALTRO']),
  context: z.enum(['SEGRETERIA', 'LABORATORIO', 'CLASSE']),
  user_type: z.enum(['GOVERNANCE', 'SEGRETERIA', 'DOCENTI', 'ALUNNI']),
  includes_minors: z.boolean().default(false),
  minors_age_band: z.enum(['U14', 'A14_17', 'BOTH']).optional(),
  parental_consent: z.boolean().default(false),
  estimated_users: z.preprocess(
    (val) => (val === '' || val === null || val === undefined || Number.isNaN(val) ? undefined : Number(val)),
    z.number().positive('Deve essere positivo').optional()
  ),
  usage_frequency: z.string().optional(),
  data_inputs: z.object({
    prompt_liberi: z.boolean().default(false),
    dati_anagrafici: z.boolean().default(false),
    dati_valutazione: z.boolean().default(false),
    file_allegati: z.boolean().default(false),
    file_tipo: z.string().optional(),
    dati_biometrici: z.boolean().default(false),
    dati_comportamentali: z.boolean().default(false),
    nessun_dato_personale: z.boolean().default(false),
  }),
  data_notes: z.string().optional(),
}).refine((d) => !d.includes_minors || !!d.minors_age_band, {
  message: 'Fascia d\'età richiesta (L. 132/2025)',
  path: ['minors_age_band'],
})

type FormData = z.infer<typeof schema>

// ── Step Components ────────────────────────────────────────────────────────────

const STEPS = ['step0Title', 'step1Title', 'step2Title', 'step3Title', 'step4Title'] as const

const PROHIBITED_KEYS = [
  'manipolazione_subliminale', 'sfruttamento_vulnerabilita', 'social_scoring',
  'scraping_facciale', 'riconoscimento_emozioni', 'categorizzazione_biometrica',
] as const

function StepIndicator({ current, steps }: { current: number; steps: string[] }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2 mb-8">
      {steps.map((key, i) => (
        <div key={i} className="flex items-center">
          <div className={cn(
            'flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold border-2 transition-colors',
            i < current
              ? 'bg-verde border-verde text-white'
              : i === current
              ? 'bg-mim-blue border-mim-blue text-white'
              : 'bg-background border-muted-foreground/30 text-muted-foreground'
          )}>
            {i < current ? <CheckCircle className="w-4 h-4" /> : i + 1}
          </div>
          <span className={cn(
            'hidden md:block ml-2 text-sm',
            i === current ? 'font-medium text-foreground' : 'text-muted-foreground'
          )}>
            {t(`assessment.${key}` as any)}
          </span>
          {i < steps.length - 1 && (
            <div className={cn(
              'mx-2 md:mx-3 h-0.5 w-6 md:w-12',
              i < current ? 'bg-verde' : 'bg-muted-foreground/30'
            )} />
          )}
        </div>
      ))}
    </div>
  )
}

const DRAFT_KEY = 'varia-assessment-draft'
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000  // discard drafts older than a week

interface StoredDraft {
  values: Partial<FormData>
  step: number
  savedAt: number
}

function loadDraft(): StoredDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    if (!raw) return null
    const draft = JSON.parse(raw) as StoredDraft
    if (Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) {
      localStorage.removeItem(DRAFT_KEY)
      return null
    }
    return draft
  } catch {
    localStorage.removeItem(DRAFT_KEY)
    return null
  }
}

export default function NewAssessment() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: entitlements } = useEntitlements()
  const { user } = useAuthStore()
  // Docente/segreteria (richiedenti) non ricevono il report PDF: l'esito è
  // consultabile solo a video, il report resta alla catena DPO/Dirigente.
  const isRequester = user?.role === 'DOCENTE' || user?.role === 'SEGRETERIA'
  const quotaExhausted =
    !!entitlements &&
    entitlements.quota != null &&
    (entitlements.assessments_remaining ?? 0) <= 0
  const [draft] = useState(loadDraft)
  const [step, setStep] = useState(draft?.step ?? 0)
  const [draftRestored, setDraftRestored] = useState(!!draft)
  const [appSearchQuery, setAppSearchQuery] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const saveTimer = useRef<ReturnType<typeof setTimeout>>()

  const { control, register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      prohibited_practices: {
        manipolazione_subliminale: false,
        sfruttamento_vulnerabilita: false,
        social_scoring: false,
        scraping_facciale: false,
        riconoscimento_emozioni: false,
        categorizzazione_biometrica: false,
      },
      includes_minors: false,
      parental_consent: false,
      data_inputs: {
        prompt_liberi: false,
        dati_anagrafici: false,
        dati_valutazione: false,
        file_allegati: false,
        dati_biometrici: false,
        dati_comportamentali: false,
        nessun_dato_personale: false,
      },
      ...(draft?.values as Partial<FormData> | undefined),
    },
  })

  const formValues = watch()
  const stepRef = useRef(step)

  // Draft autosave: debounced write of form values + current step
  useEffect(() => {
    const subscription = watch((values) => {
      clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => {
        const payload: StoredDraft = { values: values as Partial<FormData>, step: stepRef.current, savedAt: Date.now() }
        localStorage.setItem(DRAFT_KEY, JSON.stringify(payload))
      }, 800)
    })
    return () => {
      subscription.unsubscribe()
      clearTimeout(saveTimer.current)
    }
  }, [watch])

  // step changes should persist too, without waiting for a field edit
  useEffect(() => {
    stepRef.current = step
    const raw = localStorage.getItem(DRAFT_KEY)
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as StoredDraft
        parsed.step = step
        parsed.savedAt = Date.now()
        localStorage.setItem(DRAFT_KEY, JSON.stringify(parsed))
      } catch { /* corrupted draft, autosave will rewrite it */ }
    }
  }, [step])

  const discardDraft = () => {
    localStorage.removeItem(DRAFT_KEY)
    setDraftRestored(false)
    setStep(0)
    reset()
    toast({ title: 'Bozza eliminata, wizard azzerato' })
  }

  // App search autocomplete
  const { data: appSuggestions } = useQuery({
    queryKey: ['apps-search', appSearchQuery],
    queryFn: () => appsApi.search(appSearchQuery),
    enabled: appSearchQuery.length > 1,
  })

  // Create assessment mutation
  const { mutate: createAssessment, isPending } = useMutation({
    mutationFn: assessmentsApi.create,
    onSuccess: (data) => {
      localStorage.removeItem(DRAFT_KEY)
      toast({ title: t('assessment.analyzing'), description: 'L\'analisi è in corso…' })
      navigate(`/assessment/${data.id}`)
    },
    onError: (err: any) => {
      const msg = err?.response?.status === 429
        ? t('errors.rateLimit')
        : err?.response?.status === 409
        ? (err?.response?.data?.detail ?? t('errors.analysisError'))
        : t('errors.analysisError')
      toast({ title: t('common.error'), description: msg, variant: 'destructive' })
    },
  })

  const onInvalid = (formErrors: any) => {
    console.error("Validation errors:", formErrors)
    let errorStep = -1
    if (formErrors.app_name || formErrors.vendor || formErrors.website_url || formErrors.category) errorStep = 2
    else if (formErrors.context || formErrors.user_type || formErrors.minors_age_band || formErrors.estimated_users || formErrors.usage_frequency) errorStep = 3
    else if (formErrors.data_inputs || formErrors.data_notes) errorStep = 4

    toast({
      title: 'Dati incompleti o non validi',
      description: errorStep > 0 ? `Controlla e correggi gli errori nel Passaggio ${errorStep}.` : 'Controlla i dati inseriti.',
      variant: 'destructive'
    })

    if (errorStep > 0) setStep(errorStep - 1)
  }

  const onSubmit = handleSubmit((data) => {
    if (quotaExhausted) {
      toast({
        title: t(entitlements?.plan === 'FREE' ? 'quota.exhaustedFree' : 'quota.exhaustedSchool'),
        variant: 'destructive',
      })
      return
    }
    createAssessment(data as AssessmentFormData)
  }, onInvalid)

  const prohibitedFlagged = PROHIBITED_KEYS.some(
    (key) => formValues.prohibited_practices?.[key]
  )

  const canGoNext = () => {
    if (step === 1) return !!formValues.app_name && !!formValues.category
    if (step === 2) {
      if (!formValues.context || !formValues.user_type) return false
      if (formValues.includes_minors && !formValues.minors_age_band) return false
      return true
    }
    return true
  }

  // ── STEP 0: Prohibited Practices Screening (Art. 5 AI Act) ──

  const step0Content = (
    <div className="space-y-5">
      <div className="p-3 bg-mim-lightblue border border-mim-blue/30 rounded-lg text-sm text-mim-blue dark:bg-primary/10">
        {t('assessment.prohibitedIntro')}
      </div>

      <div>
        <Label className="text-base">{t('assessment.prohibitedQuestion')}</Label>
        <div className="space-y-2 mt-3">
          {PROHIBITED_KEYS.map((key) => (
            <div
              key={key}
              className={cn(
                'flex items-start gap-3 p-3 rounded-lg border transition-colors',
                formValues.prohibited_practices?.[key]
                  ? 'border-rosso bg-rosso-light/40'
                  : 'border-border hover:bg-muted/30'
              )}
            >
              <Controller
                name={`prohibited_practices.${key}` as any}
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id={`pp_${key}`}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="mt-0.5"
                  />
                )}
              />
              <label htmlFor={`pp_${key}`} className="text-sm cursor-pointer flex-1">
                {t(`assessment.prohibitedPractices.${key}` as any)}
              </label>
            </div>
          ))}
        </div>
      </div>

      {prohibitedFlagged ? (
        <div className="p-4 bg-rosso-light border border-rosso/40 rounded-lg">
          <p className="text-sm font-bold text-rosso-dark flex items-center gap-2">
            <Ban className="w-4 h-4" />
            {t('assessment.prohibitedWarningTitle')}
          </p>
          <p className="text-xs text-rosso-dark mt-1">
            {t('assessment.prohibitedWarningBody')}
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-verde" />
          {t('assessment.prohibitedNone')}
        </p>
      )}
    </div>
  )

  // ── STEP 1: App Identification ──

  const step1Content = (
    <div className="space-y-5">
      <div>
        <Label htmlFor="app_name">{t('assessment.appName')} *</Label>
        <div className="relative mt-1.5">
          <Input
            id="app_name"
            placeholder={t('assessment.appNamePlaceholder')}
            {...register('app_name')}
            onChange={(e) => {
              register('app_name').onChange(e)
              setAppSearchQuery(e.target.value)
              setShowSuggestions(true)
            }}
            onFocus={() => setShowSuggestions(true)}
          />
          <Search className="absolute right-3 top-2.5 w-4 h-4 text-muted-foreground" />
          {errors.app_name && (
            <p className="text-xs text-destructive mt-1">{errors.app_name.message}</p>
          )}
          {/* Autocomplete suggestions */}
          {showSuggestions && appSuggestions && appSuggestions.length > 0 && (
            <div className="absolute z-10 top-full left-0 right-0 bg-popover border border-border rounded-lg shadow-lg mt-1 overflow-hidden">
              {appSuggestions.map((app) => (
                <button
                  key={app.id}
                  type="button"
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted text-sm text-left"
                  onClick={() => {
                    setValue('app_name', app.name)
                    if (app.vendor) setValue('vendor', app.vendor)
                    if (app.website_url) setValue('website_url', app.website_url)
                    setValue('category', app.category)
                    setShowSuggestions(false)
                  }}
                >
                  <span className="font-medium">{app.name}</span>
                  {app.vendor && <span className="text-muted-foreground">{app.vendor}</span>}
                  <Badge variant="outline" className="ml-auto text-xs">{app.category}</Badge>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="vendor">{t('assessment.vendor')}</Label>
          <Input
            id="vendor"
            placeholder={t('assessment.vendorPlaceholder')}
            className="mt-1.5"
            {...register('vendor')}
          />
        </div>
        <div>
          <Label htmlFor="website_url">{t('assessment.websiteUrl')}</Label>
          <Input
            id="website_url"
            placeholder={t('assessment.websiteUrlPlaceholder')}
            type="url"
            className="mt-1.5"
            {...register('website_url')}
          />
          {errors.website_url && (
            <p className="text-xs text-destructive mt-1">{errors.website_url.message}</p>
          )}
        </div>
      </div>

      <div>
        <Label>{t('assessment.category')} *</Label>
        <Controller
          name="category"
          control={control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger className="mt-1.5">
                <SelectValue placeholder="Seleziona categoria…" />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(t('assessment.categories', { returnObjects: true })) as [string, string][]).map(
                  ([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          )}
        />
        {errors.category && (
          <p className="text-xs text-destructive mt-1">{t('common.required')}</p>
        )}
      </div>
    </div>
  )

  // ── STEP 2: Context ──

  const step2Content = (
    <div className="space-y-5">
      <div>
        <Label>{t('assessment.context')} *</Label>
        <Controller
          name="context"
          control={control}
          render={({ field }) => (
            <div className="grid grid-cols-1 gap-3 mt-1.5">
              {(Object.entries(t('assessment.contexts', { returnObjects: true })) as [string, string][]).map(
                ([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => field.onChange(key)}
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-lg border-2 text-sm text-left transition-colors',
                      field.value === key
                        ? 'border-mim-blue bg-mim-lightblue text-mim-blue dark:bg-primary/10'
                        : 'border-border hover:border-muted-foreground/40'
                    )}
                  >
                    <div className={cn(
                      'w-4 h-4 rounded-full border-2 flex-shrink-0',
                      field.value === key ? 'bg-mim-blue border-mim-blue' : 'border-muted-foreground/40'
                    )} />
                    {label}
                  </button>
                )
              )}
            </div>
          )}
        />
        {errors.context && <p className="text-xs text-destructive mt-1">{t('common.required')}</p>}
      </div>

      <div>
        <Label>{t('assessment.userType')} *</Label>
        <Controller
          name="user_type"
          control={control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger className="mt-1.5">
                <SelectValue placeholder="Seleziona tipologia utenti…" />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(t('assessment.userTypes', { returnObjects: true })) as [string, string][]).map(
                  ([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          )}
        />
        {errors.user_type && <p className="text-xs text-destructive mt-1">{t('common.required')}</p>}
      </div>

      <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
        <Controller
          name="includes_minors"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="includes_minors"
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
        <label htmlFor="includes_minors" className="text-sm cursor-pointer flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-giallo" />
          {t('assessment.includesMinors')}
        </label>
      </div>

      {formValues.includes_minors && (
        <>
          <div className="p-3 bg-giallo-light border border-giallo/30 rounded-lg text-sm text-giallo-dark">
            ⚠️ La presenza di utenti minorenni attiva criteri di valutazione più stringenti in conformità
            con l'Art. 4 L. 132/2025, il §3.2 DM 166/2025 e le disposizioni AI Act per la tutela dei minori.
          </div>

          <div>
            <Label>{t('assessment.minorsAgeBand')} *</Label>
            <Controller
              name="minors_age_band"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-1 gap-2 mt-1.5">
                  {(['U14', 'A14_17', 'BOTH'] as const).map((band) => (
                    <button
                      key={band}
                      type="button"
                      onClick={() => field.onChange(band)}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg border-2 text-sm text-left transition-colors',
                        field.value === band
                          ? 'border-mim-blue bg-mim-lightblue text-mim-blue dark:bg-primary/10'
                          : 'border-border hover:border-muted-foreground/40'
                      )}
                    >
                      <div className={cn(
                        'w-4 h-4 rounded-full border-2 flex-shrink-0',
                        field.value === band ? 'bg-mim-blue border-mim-blue' : 'border-muted-foreground/40'
                      )} />
                      {t(`assessment.minorsAgeBands.${band}` as any)}
                    </button>
                  ))}
                </div>
              )}
            />
            {errors.minors_age_band && (
              <p className="text-xs text-destructive mt-1">{errors.minors_age_band.message}</p>
            )}
          </div>

          {(formValues.minors_age_band === 'U14' || formValues.minors_age_band === 'BOTH') && (
            <>
              <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
                <Controller
                  name="parental_consent"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="parental_consent"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <label htmlFor="parental_consent" className="text-sm cursor-pointer">
                  {t('assessment.parentalConsent')}
                </label>
              </div>
              {!formValues.parental_consent && (
                <div className="p-3 bg-rosso-light border border-rosso/30 rounded-lg text-xs text-rosso-dark">
                  ⚠️ {t('assessment.parentalConsentWarning')}
                </div>
              )}
            </>
          )}

          {(formValues.minors_age_band === 'A14_17' || formValues.minors_age_band === 'BOTH') && (
            <div className="p-3 bg-mim-lightblue border border-mim-blue/30 rounded-lg text-xs text-mim-blue dark:bg-primary/10">
              ℹ️ {t('assessment.a1417Info')}
            </div>
          )}
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="estimated_users">{t('assessment.estimatedUsers')}</Label>
          <Input
            id="estimated_users"
            type="number"
            min={1}
            className="mt-1.5"
            {...register('estimated_users', { valueAsNumber: true })}
          />
        </div>
        <div>
          <Label htmlFor="usage_frequency">{t('assessment.usageFrequency')}</Label>
          <Input
            id="usage_frequency"
            placeholder="Es. Quotidiana, Settimanale…"
            className="mt-1.5"
            {...register('usage_frequency')}
          />
        </div>
      </div>
    </div>
  )

  // ── STEP 3: Data ──

  const step3Content = (() => {
    const dataInputKeys = [
      'prompt_liberi', 'dati_anagrafici', 'dati_valutazione',
      'file_allegati', 'dati_biometrici', 'dati_comportamentali', 'nessun_dato_personale'
    ] as const

    return (
      <div className="space-y-5">
        <div>
          <Label className="text-base">{t('assessment.dataInputs')}</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Seleziona tutti i tipi di dati che verranno inseriti nell'applicazione.
          </p>
          <div className="space-y-2 mt-3">
            {dataInputKeys.map((key) => {
              const isBiometric = key === 'dati_biometrici'
              const isComportamentale = key === 'dati_comportamentali'
              return (
                <div
                  key={key}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border transition-colors',
                    (isBiometric || isComportamentale)
                      ? 'border-rosso/30 bg-rosso-light/30'
                      : 'border-border hover:bg-muted/30'
                  )}
                >
                  <Controller
                    name={`data_inputs.${key}` as any}
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id={key}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="mt-0.5"
                      />
                    )}
                  />
                  <label htmlFor={key} className="text-sm cursor-pointer flex-1">
                    <span>{t(`assessment.dataInputTypes.${key}` as any)}</span>
                    {isBiometric && (
                      <span className="ml-2 text-xs font-medium text-rosso-dark bg-rosso-light px-1.5 py-0.5 rounded">
                        Rischio elevato AI Act
                      </span>
                    )}
                    {isComportamentale && (
                      <span className="ml-2 text-xs font-medium text-giallo-dark bg-giallo-light px-1.5 py-0.5 rounded">
                        Richiede attenzione DPIA
                      </span>
                    )}
                  </label>
                </div>
              )
            })}
          </div>
        </div>

        {formValues.data_inputs?.file_allegati && (
          <div>
            <Label htmlFor="file_tipo">Tipo di file (specificare)</Label>
            <Input
              id="file_tipo"
              placeholder="Es. PDF elaborati studenti, registrazioni audio…"
              className="mt-1.5"
              {...register('data_inputs.file_tipo')}
            />
          </div>
        )}

        <div>
          <Label htmlFor="data_notes">{t('assessment.dataNotes')}</Label>
          <Textarea
            id="data_notes"
            placeholder="Eventuali note aggiuntive sul trattamento dati…"
            className="mt-1.5"
            rows={3}
            {...register('data_notes')}
          />
        </div>

        {formValues.data_inputs?.dati_biometrici && formValues.includes_minors && (
          <div className="p-4 bg-rosso-light border border-rosso/40 rounded-lg">
            <p className="text-sm font-bold text-rosso-dark flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Attenzione: Dati biometrici + Utenti minorenni
            </p>
            <p className="text-xs text-rosso-dark mt-1">
              Questa combinazione determina automaticamente una classificazione <strong>RISCHIO ELEVATO</strong>
              ai sensi dell'AI Act (Allegato III) e richiede obbligatoriamente una DPIA.
              Il verdetto sarà ROSSO indipendentemente dagli altri punteggi.
            </p>
          </div>
        )}
      </div>
    )
  })()

  // ── STEP 4: Review ──

  const step4Content = (
    <div className="space-y-4">
      {prohibitedFlagged && (
        <div className="p-4 bg-rosso-light border-2 border-rosso rounded-lg">
          <p className="text-sm font-bold text-rosso-dark flex items-center gap-2">
            <Ban className="w-4 h-4" />
            {t('assessment.prohibitedWarningTitle')} – {t('assessment.verdictLabels.VIETATO')}
          </p>
          <p className="text-xs text-rosso-dark mt-1">
            {t('assessment.prohibitedWarningBody')}
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Globe className="w-4 h-4 text-mim-blue" />
              Applicazione
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 text-sm space-y-1">
            <p><span className="text-muted-foreground">Nome:</span> <strong>{formValues.app_name}</strong></p>
            {formValues.vendor && <p><span className="text-muted-foreground">Vendor:</span> {formValues.vendor}</p>}
            {formValues.website_url && (
              <p><span className="text-muted-foreground">URL:</span> {formValues.website_url}</p>
            )}
            <p><span className="text-muted-foreground">Categoria:</span> {formValues.category}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-mim-blue" />
              Contesto
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 text-sm space-y-1">
            <p><span className="text-muted-foreground">Contesto:</span> {formValues.context}</p>
            <p><span className="text-muted-foreground">Utenti:</span> {formValues.user_type}</p>
            <p>
              <span className="text-muted-foreground">Minori:</span>{' '}
              {formValues.includes_minors ? (
                <span className="text-giallo-dark font-medium">
                  Sì ⚠️ {formValues.minors_age_band && `(${t(`assessment.minorsAgeBands.${formValues.minors_age_band}` as any)})`}
                </span>
              ) : 'No'}
            </p>
            {formValues.includes_minors &&
              (formValues.minors_age_band === 'U14' || formValues.minors_age_band === 'BOTH') && (
              <p>
                <span className="text-muted-foreground">Consenso genitoriale:</span>{' '}
                {formValues.parental_consent ? 'Previsto' : (
                  <span className="text-rosso-dark font-medium">Non previsto ⚠️</span>
                )}
              </p>
            )}
            {formValues.estimated_users && (
              <p><span className="text-muted-foreground">N° utenti:</span> {formValues.estimated_users}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <Database className="w-4 h-4 text-mim-blue" />
            Dati Trattati
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="flex flex-wrap gap-2">
            {Object.entries(formValues.data_inputs || {})
              .filter(([key, val]) => val === true && key !== 'nessun_dato_personale')
              .map(([key]) => (
                <Badge
                  key={key}
                  variant="outline"
                  className={cn(
                    key === 'dati_biometrici' && 'border-rosso text-rosso-dark',
                    key === 'dati_comportamentali' && 'border-giallo text-giallo-dark',
                  )}
                >
                  {t(`assessment.dataInputTypes.${key}` as any)}
                </Badge>
              ))}
            {formValues.data_inputs?.nessun_dato_personale && (
              <Badge variant="outline" className="border-verde text-verde-dark">
                Nessun dato personale
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="p-4 bg-mim-lightblue border border-mim-blue/30 rounded-lg">
        <p className="text-sm text-mim-blue font-medium flex items-center gap-2">
          <ClipboardList className="w-4 h-4" />
          Cosa succederà dopo l'avvio:
        </p>
        <ol className="text-xs text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
          <li>Ricerca web automatica su fonti ufficiali (privacy policy, certificazioni EU, incidenti…)</li>
          <li>Analisi regolatoria con IA basata su AI Act e Linee Guida MIM DM 166/2025</li>
          <li>Calcolo punteggi per 5 dimensioni con riferimenti normativi citati</li>
          <li>Generazione del verdetto (VERDE/GIALLO/ROSSO) e piano di mitigazione</li>
          {isRequester ? (
            <li>Esito consultabile a video · la richiesta passa alla catena di
              validazione (DPO e Dirigente)</li>
          ) : entitlements?.can_download_pdf ? (
            <li>Produzione del report PDF scaricabile</li>
          ) : (
            <li>Verdetto visualizzato a schermo · nessun report PDF con il
              piano Gratuito (incluso a partire dal piano d'istituto)</li>
          )}
        </ol>
      </div>
    </div>
  )

  const steps = [step0Content, step1Content, step2Content, step3Content, step4Content]
  const stepIcons = [Ban, Globe, Users, Database, ClipboardList]
  const lastStep = steps.length - 1

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t('assessment.new')}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Valutazione di conformità AI Act + MIM DM 166/2025
        </p>
      </div>

      <QuotaBanner className="mb-4" />

      {draftRestored && (
        <div className="flex items-center gap-3 p-3 bg-mim-lightblue border border-mim-blue/30 rounded-lg text-sm text-mim-blue dark:bg-primary/10">
          <ClipboardList className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">Bozza precedente ripristinata automaticamente.</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-mim-blue hover:text-rosso"
            onClick={discardDraft}
          >
            Ricomincia da zero
          </Button>
        </div>
      )}

      <StepIndicator current={step} steps={[...STEPS]} />

      <form onSubmit={onSubmit}>
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              {(() => { const Icon = stepIcons[step]; return <Icon className="w-5 h-5 text-mim-blue" /> })()}
              {t(`assessment.${STEPS[step]}` as any)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {steps[step]}
          </CardContent>
        </Card>

        <div className="flex items-center justify-between mt-6">
          <Button
            type="button"
            variant="outline"
            disabled={step === 0}
            onClick={() => setStep((s) => s - 1)}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t('common.back')}
          </Button>

          {step < lastStep ? (
            <Button
              type="button"
              disabled={!canGoNext()}
              onClick={() => setStep((s) => s + 1)}
              className="bg-mim-blue hover:bg-blue-800 text-white"
            >
              {t('common.next')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={isPending || quotaExhausted}
              className="bg-mim-blue hover:bg-blue-800 text-white min-w-40"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('assessment.analyzing')}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('assessment.startAnalysis')}
                </>
              )}
            </Button>
          )}
        </div>
      </form>
    </div>
  )
}
